import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { CenteredSpinner } from "@/lib/components/CenteredSpinner";
import type { ServiceCategory } from "../../types";
import type { ServiceEditFormData } from "../../validation/service-edit-schema";

interface ServiceInformationFormProps {
  categories: ServiceCategory[];
  loading?: boolean;
  saving?: boolean;
}

export function ServiceInformationForm({
  categories,
  loading = false,
  saving = false,
}: ServiceInformationFormProps) {
  const { t } = useTranslation("services");
  const { control } = useFormContext<ServiceEditFormData>();

  if (loading) {
    return (
      <Card className="mb-6">
        <CardContent className="p-0">
          <CenteredSpinner minHeight="min-h-[400px]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>{t("serviceInformationForm.title")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={control}
            name="basicInfo.name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("serviceInformationForm.fields.serviceName")}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t("serviceInformationForm.placeholders.serviceName")}
                    disabled={saving}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="basicInfo.categoryId"
            render={({ field }) => {
              const selectedCategory = categories.find((cat) => cat.id === field.value);
              return (
                <FormItem>
                  <FormLabel>{t("serviceInformationForm.fields.category")}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value} disabled={saving}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t("serviceInformationForm.placeholders.category")}
                        >
                          {selectedCategory?.name ||
                            t("serviceInformationForm.placeholders.category")}
                        </SelectValue>
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          <FormField
            control={control}
            name="basicInfo.duration"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("serviceInformationForm.fields.duration")}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min="0"
                    disabled={saving}
                    {...field}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={control}
          name="basicInfo.description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("serviceInformationForm.fields.description")}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={t("serviceInformationForm.placeholders.description")}
                  rows={3}
                  disabled={saving}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Service Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            {/* Archive field - hidden for now, will decide later */}
            {/* <FormField
              control={control}
              name="basicInfo.isActive"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Archive</FormLabel>
                    <p className="text-sm text-muted-foreground">
                      When enabled, this service is available for scheduling. Archived services are hidden from clients.
                    </p>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={saving}
                    />
                  </FormControl>
                </FormItem>
              )}
            /> */}

            <FormField
              control={control}
              name="basicInfo.allowOnlineBooking"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base">
                    {t("serviceInformationForm.fields.allowOnlineBooking")}
                  </FormLabel>
                  <FormDescription>
                    {t("serviceInformationForm.descriptions.allowOnlineBooking")}
                  </FormDescription>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={saving}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="basicInfo.isPopular"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base">
                    {t("serviceInformationForm.fields.isPopular")}
                  </FormLabel>
                  <FormDescription>
                    {t("serviceInformationForm.descriptions.isPopular")}
                  </FormDescription>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={saving}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
